/**
 * SanctionsView — OFAC screening home.
 *
 * Shows the state of both sanctions lists, lets an admin refresh them and
 * launch a screening run, and lists past runs. Selecting a run opens its
 * detailed results.
 */
import React, { useCallback, useEffect, useMemo } from 'react';
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw';
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check';
import ScanSearch from 'lucide-react/dist/esm/icons/scan-search';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import { useSanctionsStore } from '../../stores/sanctionsStore';
import { usePermissions, Permissions } from '../../../../hooks/usePermissions';
import { useUIStore } from '../../../../stores/uiStore';
import { getTranslation } from '../../../../i18n';
import {
  Alert, Badge, Button, Card, Spinner, Text,
} from '../../../../components/ui';
import { TableEmptyRow } from '../shared';
import ScreeningRunDetail from './ScreeningRunDetail';
import type { SanctionsListStatus, SanctionsListType, ScreeningRun } from '../../types/aml';

const LIST_LABEL_KEYS: Record<SanctionsListType, string> = {
  SDN: 'amlSanctionsListSdn',
  NON_SDN: 'amlSanctionsListNonSdn',
};

const RUN_STATUS_VARIANT = {
  running: 'info',
  success: 'success',
  failed: 'danger',
} as const;

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDay(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

interface ListCardProps {
  list: SanctionsListStatus;
  canManage: boolean;
  refreshing: boolean;
  onRefresh: (listType: SanctionsListType) => void;
  t: (key: string) => string;
}

const ListCard = React.memo<ListCardProps>(
  ({ list, canManage, refreshing, onRefresh, t }) => {
    const isRunning = list.status === 'running' || refreshing;
    const handleRefresh = useCallback(
      () => onRefresh(list.list_type), [onRefresh, list.list_type],
    );

    return (
      <Card className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-content-muted shrink-0" />
              <Text variant="subheading">{t(LIST_LABEL_KEYS[list.list_type])}</Text>
            </div>
            <Text variant="caption" className="mt-1 block">
              {t(`amlSanctionsListState_${list.status}`)}
            </Text>
          </div>
          {canManage ? (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<RefreshCw size={14} />}
              loading={isRunning}
              onClick={handleRefresh}
            >
              {t('amlSanctionsBtnRefresh')}
            </Button>
          ) : null}
        </div>

        <div className="mt-4 grid grid-cols-3 gap-3">
          <div>
            <Text variant="label">{t('amlSanctionsListPublished')}</Text>
            <Text variant="body" className="mt-0.5 block">
              {formatDay(list.published_date)}
            </Text>
          </div>
          <div>
            <Text variant="label">{t('amlSanctionsListEntries')}</Text>
            <Text variant="body" className="mt-0.5 block tabular-nums">
              {list.entry_count.toLocaleString()}
            </Text>
          </div>
          <div>
            <Text variant="label">{t('amlSanctionsListNames')}</Text>
            <Text variant="body" className="mt-0.5 block tabular-nums">
              {list.name_count.toLocaleString()}
            </Text>
          </div>
        </div>

        <Text variant="caption" className="mt-3 block">
          {t('amlSanctionsListUpdated')}: {formatDateTime(list.last_refreshed_at)}
        </Text>

        {list.error ? (
          <Alert variant="error" className="mt-3">
            {list.error}
          </Alert>
        ) : null}
      </Card>
    );
  },
);
ListCard.displayName = 'ListCard';

interface RunRowProps {
  run: ScreeningRun;
  onOpen: (runId: string) => void;
  t: (key: string) => string;
}

const RunRow = React.memo<RunRowProps>(({ run, onOpen, t }) => {
  const handleOpen = useCallback(() => onOpen(run.id), [onOpen, run.id]);

  return (
    <tr
      className="border-b border-subtle transition-colors hover:bg-surface-secondary/50 bg-surface-primary cursor-pointer"
      onClick={handleOpen}
    >
      <td className="px-4 py-3 text-sm text-content-secondary whitespace-nowrap">
        {formatDateTime(run.finished_at || run.started_at)}
      </td>
      <td className="px-4 py-3">
        <Badge variant={RUN_STATUS_VARIANT[run.status] ?? 'neutral'} size="sm" dot>
          {t(`amlScreeningRunStatus_${run.status}`)}
        </Badge>
      </td>
      <td className="px-4 py-3 text-sm text-content-secondary tabular-nums">
        {run.customers_screened.toLocaleString()}
      </td>
      <td className="px-4 py-3 text-sm text-status-success tabular-nums">
        {run.cleared_count.toLocaleString()}
      </td>
      <td className="px-4 py-3 text-sm tabular-nums">
        <span className={run.potential_match_count > 0 ? 'text-status-error font-semibold' : 'text-content-secondary'}>
          {run.potential_match_count.toLocaleString()}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-content-secondary tabular-nums">
        {run.threshold}%
      </td>
      <td className="px-4 py-3 text-sm text-content-muted capitalize">
        {run.triggered_by || '—'}
      </td>
      <td className="px-4 py-3 text-right">
        <ChevronRight size={16} className="text-content-muted inline-block" />
      </td>
    </tr>
  );
});
RunRow.displayName = 'RunRow';

const SanctionsView: React.FC = () => {
  const {
    lists, listsLoading, refreshing,
    runs, runsLoading, screening, activeRun,
    error,
    fetchLists, refreshLists, fetchRuns, startScreening, openRun,
  } = useSanctionsStore();

  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permissions.AML_SANCTIONS_MANAGE);
  const { language } = useUIStore();
  const t = useCallback((key: string) => getTranslation(language, key), [language]);

  useEffect(() => {
    fetchLists();
    fetchRuns(1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefreshList = useCallback(
    (listType: SanctionsListType) => { refreshLists(listType); },
    [refreshLists],
  );
  const handleRefreshAll = useCallback(() => { refreshLists(); }, [refreshLists]);
  const handleStartScreening = useCallback(() => { startScreening(80); }, [startScreening]);

  // Screening is meaningless until at least one list has been downloaded.
  const hasAnyList = useMemo(
    () => lists.some((l) => l.entry_count > 0), [lists],
  );

  if (activeRun) {
    return <ScreeningRunDetail />;
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Text variant="heading">{t('amlSanctionsTitle')}</Text>
          <Text variant="caption" className="mt-1 block">
            {t('amlSanctionsSubtitle')}
          </Text>
        </div>
        {canManage ? (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="lg"
              leftIcon={<RefreshCw size={16} />}
              loading={refreshing}
              onClick={handleRefreshAll}
            >
              {t('amlSanctionsBtnRefreshAll')}
            </Button>
            <Button
              variant="success"
              size="lg"
              leftIcon={<ScanSearch size={16} />}
              loading={screening}
              disabled={!hasAnyList}
              onClick={handleStartScreening}
            >
              {t('amlSanctionsBtnRunScreening')}
            </Button>
          </div>
        ) : null}
      </div>

      {error ? <Alert variant="error">{error}</Alert> : null}

      {!hasAnyList && !listsLoading ? (
        <Alert variant="warning">{t('amlSanctionsNoListsWarning')}</Alert>
      ) : null}

      {listsLoading && lists.length === 0 ? (
        <div className="flex items-center justify-center h-32">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {lists.map((list) => (
            <ListCard
              key={list.list_type}
              list={list}
              canManage={canManage}
              refreshing={refreshing}
              onRefresh={handleRefreshList}
              t={t}
            />
          ))}
        </div>
      )}

      <div>
        <Text variant="subheading" className="mb-3 block">
          {t('amlScreeningHistoryTitle')}
        </Text>

        <div className="rounded-xl border border-subtle bg-surface-primary overflow-hidden">
          {runsLoading && runs.length === 0 ? (
            <div className="flex items-center justify-center h-40">
              <Spinner size="lg" />
            </div>
          ) : runs.length === 0 ? (
            <TableEmptyRow title={t('amlScreeningNoRuns')} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px]">
                <thead>
                  <tr className="border-b border-subtle bg-surface-secondary/50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{t('amlScreeningColDate')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{t('amlScreeningColStatus')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{t('amlScreeningColScreened')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{t('amlScreeningColCleared')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{t('amlScreeningColPotentialMatches')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{t('amlScreeningColThreshold')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{t('amlScreeningColTriggeredBy')}</th>
                    <th className="px-4 py-3 w-[40px]" />
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <RunRow key={run.id} run={run} onOpen={openRun} t={t} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SanctionsView;
